// EN-5067: helpers for the dynamic per-ward custom fields — client-side validation (UX only, the
// server is the source of truth), building the AddPatient payload, and formatting saved values for
// the read-only side panel.

import i18next from "i18next";

import {
  CustomFieldType,
  ICustomFieldAnswer,
  ICustomFieldFormItem,
  ICustomFieldValueInput,
  IPatientCustomFieldValue,
  IPatientListCustomFieldValue,
} from "../@types/CustomField";
import { getShortFormattedUtcDate } from "./dateFormatter";

export type CustomFieldAnswers = Record<string, ICustomFieldAnswer>;
export type CustomFieldErrors = Record<string, string>;

// EN-5067: dynamic custom-field columns are stored in the patientListConfig map under this prefix.
export const CUSTOM_COLUMN_CONFIG_PREFIX = "custom:";
export function customColumnConfigKey(definitionId: string): string {
  return `${CUSTOM_COLUMN_CONFIG_PREFIX}${definitionId}`;
}

// EN-5067: a Date custom field's stored answer is in the server format, YYYY-MM-DD.
const DATE_VALUE_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;

// True only for a real calendar date in YYYY-MM-DD — rejects partials, 2026-13-01 and 2026-02-30.
export function isValidCustomFieldDate(value: string): boolean {
  const match = DATE_VALUE_PATTERN.exec(value);
  if (!match) return false;

  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const date = new Date(Date.UTC(year, month - 1, day));

  return (
    date.getUTCFullYear() === year &&
    date.getUTCMonth() === month - 1 &&
    date.getUTCDate() === day
  );
}

// The Date of birth field's messages, reused verbatim so a Date custom field reads identically.
// These are thunks, not constants: a constant evaluated at import time would resolve before
// i18next is initialised and come back as the raw key (EN-5191). The callers render whatever
// string comes back, so translating here keeps every one of them unchanged.
export const DATE_MISSING_MESSAGE = () => i18next.t("common_dateMissing");
export const DATE_INVALID_MESSAGE = () => i18next.t("common_dateInvalid");

// Mirrors shouldShowDateErrorMessage on Add Patient's Date of birth field: nothing entered and a
// partial/impossible date get the same message the DOB field shows. Two deliberate departures — a
// blank OPTIONAL custom field is valid (Date of birth is always required), and a FUTURE date is
// allowed (a custom field can legitimately be an expiry or review date). Returns "" when acceptable.
export function validateDateAnswer(value: string, isRequired: boolean): string {
  if (value === "") return isRequired ? DATE_MISSING_MESSAGE() : "";
  if (!isValidCustomFieldDate(value)) return DATE_INVALID_MESSAGE();

  return "";
}

function isBlankAnswer(
  item: ICustomFieldFormItem,
  answer?: ICustomFieldAnswer
): boolean {
  // A YesNo toggle always has a boolean state (off = false), so it is never "blank".
  if (item.type === "YesNo") return false;
  if (item.type === "Multiselect") {
    return !answer?.selectedOptions || answer.selectedOptions.length === 0;
  }
  const value = answer?.value;
  return value === undefined || value === null || value.trim() === "";
}

// A YesNo answer normalised to the server format ("true"/"false"); off/unset reads as "false".
function yesNoValue(answer?: ICustomFieldAnswer): string {
  return answer?.value === "true" ? "true" : "false";
}

// Client-side mirror of the server validation. Returns a map of customFieldDefinitionId -> message
// for the fields currently shown; an empty map means the section is valid.
export function validateCustomFields(
  items: ICustomFieldFormItem[],
  answers: CustomFieldAnswers
): CustomFieldErrors {
  const errors: CustomFieldErrors = {};

  items.forEach((item) => {
    const answer = answers[item.customFieldDefinitionId];
    const blank = isBlankAnswer(item, answer);

    // Date owns its whole rule set (blank included) so it matches the Date of birth field above it.
    if (item.type === "Date") {
      const message = validateDateAnswer(
        (answer?.value ?? "").trim(),
        item.isRequired
      );
      if (message) {
        errors[item.customFieldDefinitionId] = message;
      }
      return;
    }

    if (blank) {
      if (item.isRequired) {
        errors[item.customFieldDefinitionId] = i18next.t(
          "customField_validation_required",
          { field: item.label }
        );
      }
      return; // nothing else to validate on an empty optional field
    }

    if (item.type === "Multiselect") {
      const invalid = (answer?.selectedOptions ?? []).some(
        (option) => !item.options.includes(option)
      );
      if (invalid) {
        errors[item.customFieldDefinitionId] = i18next.t(
          "customField_validation_invalidOption",
          { field: item.label }
        );
      }
      return;
    }

    const value = (answer?.value ?? "").trim();
    const { validation } = item;

    switch (item.type) {
      case "Integer": {
        if (!/^-?\d+$/.test(value)) {
          errors[item.customFieldDefinitionId] = i18next.t(
            "customField_validation_wholeNumber",
            { field: item.label }
          );
          return;
        }
        break;
      }
      case "Decimal": {
        if (!/^-?\d+(\.\d+)?$/.test(value)) {
          errors[item.customFieldDefinitionId] = i18next.t(
            "customField_validation_number",
            { field: item.label }
          );
          return;
        }
        break;
      }
      case "FreeText": {
        if (
          validation.maxLength !== undefined &&
          value.length > validation.maxLength
        ) {
          errors[item.customFieldDefinitionId] = i18next.t(
            "customField_validation_maxLength",
            { field: item.label, max: validation.maxLength }
          );
          return;
        }
        if (validation.regex) {
          let matches = true;
          try {
            matches = new RegExp(validation.regex).test(value);
          } catch {
            matches = true; // a malformed pattern shouldn't block the user; the server decides.
          }
          if (!matches) {
            errors[item.customFieldDefinitionId] =
              validation.regexMessage ||
              i18next.t("customField_validation_format", {
                field: item.label,
              });
            return;
          }
        }
        break;
      }
      case "Dropdown": {
        if (!item.options.includes(value)) {
          errors[item.customFieldDefinitionId] = i18next.t(
            "customField_validation_invalidOption",
            { field: item.label }
          );
          return;
        }
        break;
      }
      default:
        break;
    }

    // Numeric range applies to Integer/Decimal.
    if (item.type === "Integer" || item.type === "Decimal") {
      const numeric = Number(value);
      if (validation.minValue !== undefined && numeric < validation.minValue) {
        errors[item.customFieldDefinitionId] = i18next.t(
          "customField_validation_minValue",
          { field: item.label, min: validation.minValue }
        );
      } else if (
        validation.maxValue !== undefined &&
        numeric > validation.maxValue
      ) {
        errors[item.customFieldDefinitionId] = i18next.t(
          "customField_validation_maxValue",
          { field: item.label, max: validation.maxValue }
        );
      }
    }
  });

  return errors;
}

// Build the AddPatient customFields payload from ONLY the fields currently shown. Scalars send
// `value`; Multiselect sends `selectedOptions` and leaves `value` unset. Blank optional fields are
// omitted. Values are expected already in the server format (Date YYYY-MM-DD, YesNo "true"/"false").
export function buildCustomFieldsPayload(
  items: ICustomFieldFormItem[],
  answers: CustomFieldAnswers
): ICustomFieldValueInput[] {
  const payload: ICustomFieldValueInput[] = [];

  items.forEach((item) => {
    const answer = answers[item.customFieldDefinitionId];

    if (isBlankAnswer(item, answer)) return;

    if (item.type === "Multiselect") {
      payload.push({
        customFieldDefinitionId: item.customFieldDefinitionId,
        selectedOptions: answer?.selectedOptions ?? [],
      });
      return;
    }

    payload.push({
      customFieldDefinitionId: item.customFieldDefinitionId,
      value:
        item.type === "YesNo"
          ? yesNoValue(answer)
          : (answer?.value ?? "").trim(),
    });
  });

  return payload;
}

// EN-5067: build the PUT payload for EDITED side-panel custom fields. Unlike
// buildCustomFieldsPayload (Add Patient, which omits blank optionals), edits must SEND cleared
// fields as empty so the server removes them. Pass only the fields that actually changed.
export function buildCustomFieldEditPayload(
  items: ICustomFieldFormItem[],
  answers: CustomFieldAnswers
): ICustomFieldValueInput[] {
  return items.map((item) => {
    const answer = answers[item.customFieldDefinitionId] ?? {};

    if (item.type === "Multiselect") {
      return {
        customFieldDefinitionId: item.customFieldDefinitionId,
        selectedOptions: answer.selectedOptions ?? [],
      };
    }

    return {
      customFieldDefinitionId: item.customFieldDefinitionId,
      value:
        item.type === "YesNo"
          ? yesNoValue(answer)
          : (answer.value ?? "").trim(),
    };
  });
}

// Map a server ErrorResponse message back to a field by its "Label: ..." / "Label is required."
// prefix. Returns a map of customFieldDefinitionId -> message; unmatched messages are returned
// separately so the caller can surface them at the section level.
export function mapCustomFieldErrorsByLabel(
  items: ICustomFieldFormItem[],
  messages: string[]
): { fieldErrors: CustomFieldErrors; sectionErrors: string[] } {
  const fieldErrors: CustomFieldErrors = {};
  const sectionErrors: string[] = [];

  messages.forEach((message) => {
    const match = items.find(
      (item) =>
        message.startsWith(`${item.label}:`) ||
        message.startsWith(`${item.label} `)
    );
    if (match) {
      fieldErrors[match.customFieldDefinitionId] = message;
    } else {
      sectionErrors.push(message);
    }
  });

  return { fieldErrors, sectionErrors };
}

// Core display formatter shared by the side panel and the patient-list columns.
function formatByType(
  type: CustomFieldType,
  value: string | null,
  selectedOptions: string[]
): string {
  if (type === "Multiselect") {
    return selectedOptions.length > 0 ? selectedOptions.join(", ") : "-";
  }

  const trimmed = value?.trim();
  if (!trimmed) return "-";

  if (type === "YesNo") {
    return i18next.t(trimmed === "true" ? "common_yes" : "common_no");
  }

  if (type === "Date") {
    // The value is a plain calendar date (YYYY-MM-DD); format as UTC to avoid a tz day-shift.
    return getShortFormattedUtcDate(trimmed);
  }

  return trimmed;
}

// Format a saved value for the read-only side-panel card description.
export function formatCustomFieldDisplay(
  field: IPatientCustomFieldValue
): string {
  return formatByType(field.type, field.value, field.selectedOptions ?? []);
}

// Format a patient-list custom-column value (scalar types). Multiselect is rendered as chips by the
// cell, not via this helper. Returns "-" when the patient has no value for the column.
export function formatCustomColumnValue(
  type: CustomFieldType,
  value: IPatientListCustomFieldValue | undefined
): string {
  if (!value) return "-";
  return formatByType(type, value.value, value.selectedOptions ?? []);
}
